// Accessi ai portali che il robot usa per scaricare le fatture (Temu, Metro,
// ManoMano, Hostinger). Li scrive solo l'amministratore; la password non
// torna mai nella pagina, si vede solo se c'e'.
import { useCallback, useEffect, useState } from "react";

import { prettifyApiError } from "../../../shared/api";
import { usePermessi } from "../../../shared/auth/permessi";
import { fetchAccessi, salvaAccesso, type AccessoPortale } from "../fattureEstereApi";

const CAMPO =
  "h-9 w-full rounded-md border border-line-2 bg-card px-2.5 text-[13px] text-ink outline-none focus-visible:ring-2 focus-visible:ring-ink-4 max-md:h-11 max-md:text-[15px]";

function Modulo({ accesso, onSalvato }: { accesso: AccessoPortale; onSalvato: () => void }) {
  const [utente, setUtente] = useState(accesso.utente ?? "");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [togliCodice, setTogliCodice] = useState(false);
  const [stato, setStato] = useState<"fermo" | "salvo" | "errore">("fermo");
  const [errore, setErrore] = useState<string | null>(null);

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    setStato("salvo");
    setErrore(null);
    try {
      await salvaAccesso(accesso.portale, {
        utente: utente.trim(),
        ...(password ? { password } : {}),
        ...(totp ? { totp } : togliCodice ? { totp: "" } : {}),
      });
      setPassword("");
      setTotp("");
      setStato("fermo");
      onSalvato();
    } catch (err) {
      setErrore(prettifyApiError(err));
      setStato("errore");
    }
  }

  const id = (campo: string) => `accesso-${accesso.portale}-${campo}`;
  return (
    <form onSubmit={(e) => void salva(e)} className="mt-2 grid gap-2 sm:grid-cols-3">
      <label htmlFor={id("utente")} className="text-[12px] font-semibold text-ink-2">
        Utente
        <input
          id={id("utente")}
          className={CAMPO}
          autoComplete="off"
          value={utente}
          onChange={(e) => setUtente(e.target.value)}
          required
        />
      </label>
      <label htmlFor={id("password")} className="text-[12px] font-semibold text-ink-2">
        Password
        <input
          id={id("password")}
          type="password"
          className={CAMPO}
          autoComplete="new-password"
          placeholder={accesso.configurato ? "Lascia vuoto per non cambiarla" : ""}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required={!accesso.configurato}
        />
      </label>
      <label htmlFor={id("totp")} className="text-[12px] font-semibold text-ink-2">
        Chiave app di autenticazione (se c'e')
        <input
          id={id("totp")}
          type="password"
          className={CAMPO}
          autoComplete="off"
          placeholder={accesso.con_codice ? "Salvata: lascia vuoto" : "Facoltativa"}
          value={totp}
          onChange={(e) => setTotp(e.target.value)}
        />
      </label>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
        <button
          type="submit"
          disabled={stato === "salvo"}
          className="inline-flex h-[30px] items-center rounded-full bg-ink px-3.5 text-[12.5px] font-semibold text-paper hover:bg-ink-2 disabled:opacity-50 max-md:h-10"
        >
          {stato === "salvo" ? "Salvo…" : "Salva"}
        </button>
        {accesso.con_codice && (
          <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-2">
            <input
              type="checkbox"
              checked={togliCodice}
              onChange={(e) => setTogliCodice(e.target.checked)}
            />
            Togli la chiave salvata
          </label>
        )}
        {errore && (
          <span role="alert" className="text-[12.5px] font-semibold text-red">
            {errore}
          </span>
        )}
      </div>
    </form>
  );
}

export function AccessiPortali() {
  const { isGod } = usePermessi();
  const [accessi, setAccessi] = useState<AccessoPortale[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [aperto, setAperto] = useState<string | null>(null);

  const ricarica = useCallback(async (signal?: AbortSignal) => {
    try {
      setAccessi(await fetchAccessi(signal));
      setErrore(null);
    } catch (err) {
      if (!signal?.aborted) setErrore(prettifyApiError(err));
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void ricarica(ctrl.signal);
    return () => ctrl.abort();
  }, [ricarica]);

  const mancanti = (accessi ?? []).filter((a) => !a.configurato).length;

  if (accessi === null && !errore) return null;

  // Vive in Impostazioni › Connessioni (Davide 28/10: niente riquadri nella
  // pagina Fatture estere, gli accessi stanno con le altre connessioni).
  return (
    <div className="px-5 py-4 max-md:px-4">
      <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">
        Accessi ai portali
        <span className="mt-0.5 block text-[12.5px] font-normal text-ink-2 max-md:text-[14px] max-md:text-ink">
          {accessi === null
            ? "accessi non letti"
            : mancanti > 0
              ? `${mancanti} da inserire: senza, il robot non scarica le loro fatture`
              : "il robot scarica le fatture da solo"}
        </span>
      </h3>
      {errore && <p className="mt-2 text-[12.5px] font-semibold text-red">{errore}</p>}
      <ul className="mt-2 divide-y divide-line">
        {(accessi ?? []).map((a) => (
          <li key={a.portale} className="py-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="w-24 text-[13.5px] font-bold text-ink max-md:text-[15px]">
                {a.nome}
              </span>
              <span
                className={`text-[12.5px] text-ink max-md:text-[14px] ${a.configurato ? "" : "rounded-full bg-amber-soft px-2 font-semibold"}`}
              >
                {a.configurato
                  ? `Utente ${a.utente}${a.con_codice ? " · con codice di verifica" : ""}`
                  : "Accesso da inserire"}
              </span>
              {isGod && (
                <button
                  type="button"
                  onClick={() => setAperto(aperto === a.portale ? null : a.portale)}
                  className="ml-auto inline-flex min-h-8 items-center text-[12.5px] font-semibold text-ink underline decoration-line-2 underline-offset-2 hover:decoration-ink-3 max-md:min-h-11 max-md:text-[14px]"
                >
                  {aperto === a.portale ? "Chiudi" : a.configurato ? "Cambia" : "Inserisci"}
                </button>
              )}
            </div>
            {isGod && aperto === a.portale && (
              <Modulo
                accesso={a}
                onSalvato={() => {
                  setAperto(null);
                  void ricarica();
                }}
              />
            )}
          </li>
        ))}
      </ul>
      {!isGod && (
        <p className="mt-1 text-[12px] text-ink-2">Gli accessi li inserisce l'amministratore.</p>
      )}
    </div>
  );
}
